import {
  Observable,
  map,
  firstValueFrom,
  catchError,
  of,
  Subject,
  defer,
  switchMap,
  from,
  EMPTY,
} from 'rxjs';
import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../environment/environment';

import { RolesInstallations, peutConsulterInstallation } from './droits-installations';

type ApiAuthentificationResponse = {
  token: string;
  refresh_token: string;
};

type ApiRafraichissementTokenResponse = {
  token: string;
};

export const AUTH_TOKEN_STORAGE_KEY = 'token';
const AUTH_REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

function getAuthStorage(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

export type AuthTokenInfo = {
  exp: number;
  iat: number;
  logged: boolean;
  nom: string;
  prenom: string;
  username: string;
  roles: string[];
  id?: number | string;
  installations?: RolesInstallations;
};

@Injectable({
  providedIn: 'root',
})
export class Authentification {
  tokenInfoSignal = signal<AuthTokenInfo | null>(null);
  headerTitreSignal = signal<string>('TECSOL ANALYTICS');
  menuLateralSignal = signal<string>('lg');

  public jwtHelper: JwtHelperService = new JwtHelperService();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  /** Incrémenté uniquement aux frontières de session, jamais lors d'un refresh normal. */
  sessionVersion = 0;
  readonly sessionChanges = new Subject<boolean>();
  /** Connexion interactive uniquement : ni restauration de session ni renouvellement JWT. */
  private readonly connexionReussie = new Subject<void>();
  readonly connexionsReussies = this.connexionReussie.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {
    const version = this.sessionVersion;
    this.getCurrentTokenInfo().then((tokenInfo) => {
      if (version !== this.sessionVersion) return;
      this.tokenInfoSignal.set(tokenInfo);
      this.planifierRafraichissement();
    });
  }

  private planifierRafraichissement(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const tokenInfo = this.tokenInfoSignal();
    if (!tokenInfo) return;

    const now = Date.now();
    const durationMs = (tokenInfo.exp - tokenInfo.iat) * 1000;
    const elapsedMs = now - tokenInfo.iat * 1000;
    const delaiMs = Math.max(0, durationMs * 0.8 - elapsedMs);

    this.refreshTimer = setTimeout(() => {
      void this.forceTokenRefresh();
    }, delaiMs);
  }

  // ======================== Gestion du Token ==============================
  async getToken(): Promise<string | null> {
    return getAuthStorage()?.getItem(AUTH_TOKEN_STORAGE_KEY) ?? null;
  }

  async isCurrentTokenValid(): Promise<boolean> {
    let currentTokenInfo = await this.getCurrentTokenInfo();

    // Si le token n'existe pas
    if (!currentTokenInfo) {
      return false;
    }

    // Si le token est à plus de 80% de la durée de vie
    let errorMargin = 0.8;
    let tokenDurationMs = (currentTokenInfo.exp - currentTokenInfo.iat) * 1000;
    let currentTimestampMs = new Date().getTime();
    let elapsedMsSinceCreation = currentTimestampMs - currentTokenInfo.iat * 1000;

    return tokenDurationMs * errorMargin > elapsedMsSinceCreation;
  }

  /** Accepte une session valide, en renouvelant d’abord un JWT proche de son expiration. */
  async sessionValide(): Promise<boolean> {
    if (await this.isCurrentTokenValid()) return true;
    if (await this.forceTokenRefresh()) return true;
    const token = await this.getToken();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  async getCurrentTokenInfo(): Promise<AuthTokenInfo | null> {
    const token = getAuthStorage()?.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      return null;
    }

    try {
      return this.jwtHelper.decodeToken<AuthTokenInfo>(token);
    } catch {
      await this.removeToken();
      return null;
    }
  }

  getUtilisateurConnecteId(): number | null {
    const tokenInfo = this.tokenInfoSignal();
    if (!tokenInfo) {
      return null;
    }

    const valeur = typeof tokenInfo.id === 'string' ? Number(tokenInfo.id) : tokenInfo.id;
    return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null;
  }

  async removeToken(): Promise<void> {
    ++this.sessionVersion;
    this.refreshPromise = null;
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    const storage = getAuthStorage();
    storage?.removeItem(AUTH_TOKEN_STORAGE_KEY);
    storage?.removeItem(AUTH_REFRESH_TOKEN_STORAGE_KEY);
    this.tokenInfoSignal.set(null);
    this.headerTitreSignal.set('TECSOL ANALYTICS');
    this.sessionChanges.next(false);
  }

  async setToken(accessToken: string, refreshToken?: string): Promise<void> {
    const info = this.jwtHelper.decodeToken<AuthTokenInfo>(accessToken);
    const previous = this.tokenInfoSignal();
    const nouvelleSession =
      refreshToken !== undefined ||
      String(previous?.id ?? previous?.username) !== String(info?.id ?? info?.username);
    if (nouvelleSession) {
      ++this.sessionVersion;
      this.refreshPromise = null;
      getAuthStorage()?.removeItem(AUTH_REFRESH_TOKEN_STORAGE_KEY);
    }
    const storage = getAuthStorage();
    storage?.setItem(AUTH_TOKEN_STORAGE_KEY, accessToken);

    if (refreshToken) {
      storage?.setItem(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }

    this.tokenInfoSignal.set(info);
    if (nouvelleSession) this.sessionChanges.next(true);
    this.planifierRafraichissement();
  }

  async forceTokenRefresh(): Promise<boolean> {
    if (this.refreshPromise !== null) {
      return this.refreshPromise;
    }

    const promise = this.execForceTokenRefresh()
      .catch(() => false)
      .finally(() => {
        if (this.refreshPromise === promise) this.refreshPromise = null;
      });
    this.refreshPromise = promise;
    return promise;
  }

  private async execForceTokenRefresh(): Promise<boolean> {
    const version = this.sessionVersion;
    const refreshTokenInStorage = getAuthStorage()?.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshTokenInStorage) {
      return false;
    }

    const pipeline = this.apiRafraichissement(refreshTokenInStorage).pipe(
      catchError((_) => of(null)),
    );
    const response = await firstValueFrom(pipeline);

    if (
      !response?.token ||
      version !== this.sessionVersion ||
      refreshTokenInStorage !== getAuthStorage()?.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
    ) {
      return false;
    }

    await this.setToken(response.token);
    return true;
  }

  logIn(login: string, passwd: string): Observable<void> {
    return defer(() => {
      void this.removeToken();
      const version = this.sessionVersion;
      return this.apiAuthentification(login, passwd).pipe(
        switchMap((data) => {
          if (version !== this.sessionVersion) return EMPTY;
          const nouvelleVersion = version + 1;
          return from(this.setToken(data.token, data.refresh_token)).pipe(
            map(() => {
              if (this.sessionVersion !== nouvelleVersion) return;
              this.connexionReussie.next();
              void this.router.navigateByUrl(
                peutConsulterInstallation(this.tokenInfoSignal()) ? '/parc' : '/assistant',
              );
            }),
          );
        }),
      );
    });
  }

  logOut() {
    const suppression = this.removeToken();
    const version = this.sessionVersion;
    suppression.then(() => {
      if (version === this.sessionVersion) void this.router.navigateByUrl('/connexion');
    });
  }

  // ======================== Headers HTTP ==============================
  prepareHeaders() {
    return {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };
  }

  // ========================== Requêtes API ================================
  apiAuthentification(login: string, passwd: string) {
    let endpoint = `${environment.apiUrl}/login/check`;
    let postData = { username: login, password: passwd };
    return this.http.post<ApiAuthentificationResponse>(endpoint, postData);
  }

  apiRafraichissement(refreshToken: string) {
    let endpoint = `${environment.apiUrl}/token/refresh`;
    let postData = { refresh_token: refreshToken };
    return this.http.post<ApiRafraichissementTokenResponse>(endpoint, postData);
  }
}

// ========================== Route Guards ================================

@Injectable({ providedIn: 'root' })
export class hasNoValidTokenGuard {
  constructor(
    private auth: Authentification,
    private router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const isValid = await this.auth.isCurrentTokenValid();

    if (!isValid) {
      return true;
    }

    const tokenInfo = await this.auth.getCurrentTokenInfo();
    this.router.navigateByUrl(peutConsulterInstallation(tokenInfo) ? '/parc' : '/assistant');
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class hasValidTokenGuard {
  constructor(
    private auth: Authentification,
    private router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let isValid = await this.auth.isCurrentTokenValid();

    if (isValid) {
      return true;
    } else {
      this.router.navigateByUrl('/connexion');
      return false;
    }
  }
}

@Injectable({ providedIn: 'root' })
export class checkTokenValidityAndRefreshIfNeededGuard {
  constructor(
    private auth: Authentification,
    private router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (await this.auth.sessionValide()) return true;

    this.router.navigateByUrl('/connexion');
    return false;
  }
}
